(ns quuppa.common.qpe.pe.universalformat.schema
  (:require [quuppa.common.qpe.schema :as q-schema]
            [clojure.string :as str]))

; kaosko 2025-05-13 - it is somewhat questionable whether we gain anything from this extra level of indirection,
; perhaps would be better to use the enum as a keyword. Would also need to change the mapping in pe.universalformat.domain
(defn encode-trigger-mode [trigger-kw]
  ;; universal-target->clj returns keyword from triggerMode and target enums
  ;; we want to encode backend values to frontend values (that can be sent to API, see below schemas).
  ;; see also `make-universal-target`.
  (case trigger-kw
    :LastSeenUpdate "lastseenupdate"
    :Interval "interval"
    :PositionUpdate "positionupdate"
    :CalculationUpdate "calculationupdate"
    :RawPositionUpdate "rawpositionupdate"
    :SmoothPositionUpdate "smoothpositionupdate"
    :KalmanPositionUpdate "kalmanpositionupdate"
    :LocationUpdate "locationupdate"
    :ButtonDownUpdate "buttondownupdate"
    (q-schema/->lower-case trigger-kw)))


;; Reitit API uses string transform, as values are on query-string
;; Websocket API uses JSON transform, though messages use Transit,
;; e.g. numbers from JS don't have types, so we need to case them to e.g. doubles.
;; use :decode/json for decode that needs to work for both HTTP and WS messages.

(def create-universal-target-schema
  [:and
   ;; TODO: could be :multi schema with dispatching on both [:target :triggerMode]?
   [:map
    {:closed false}
    [:name
     {:description "Required. Define an output format name. Must be unique within the projects output target names. Can only contain A-Z a-z - or _"}
     q-schema/simple-string?]
    [:format
     {:description "Required. Specify one of the existing output format IDs/names, whether a custom format or a built-in one."}
     :string]
    [:target
     {:description "Required. Either 'UDP' or 'File', case insensitive."
      :decode/json q-schema/->lower-case
      :decode/string q-schema/->lower-case
      :encode/string q-schema/->lower-case}
     [:enum "udp" "file" "mqtt" "websocket" "httppost"]]
    [:type
     {:description "Required. Either 'json' or 'csv', case insensitive."
      :decode/json q-schema/->lower-case
      :decode/string q-schema/->lower-case
      :encode/string q-schema/->lower-case}
     [:enum "json" "csv"]]
    [:triggerMode
     {:description "Required. One of 'Interval', 'LastSeenUpdate', 'LocationUpdate', 'RawPositionUpdate', 'SmoothPositionUpdate', 'KalmanPositionUpdate', 'CalculationUpdate' or 'ButtonDownUpdate', case insensitive.

- In **Interval** mode, the system sends  updates in regular intervals as defined by 'Interval' parameter (required for that triggerMode).
- In **LastSeenUpdate** mode, the system sends update as Tag's last seen value changes, typically when packet is received from Tag.
- In **LocationUpdate** mode, the system sends update when Tag's Location changes (basically when location timestamp changes).
- In **RawPositionUpdate** mode, the system sends update when new accurate position is calculated.
- In **SmoothPositionUpdate** mode, the system sends update when new smooth position is calculated.
- In **KalmanPositionUpdate** mode, the system sends update when new Kalman position is calculated.
- In **CalculationUpdate** mode, the system sends update when Tag's accurate location is calculated, even if the estimation fails.
- In **ButtonDownUpdate** mode, the system sends update when packet indicating that the Tag's button is down is received."
      :decode/json q-schema/->lower-case
      :decode/string q-schema/->lower-case
      :encode/string encode-trigger-mode}
     [:enum "interval" "lastseenupdate" "locationupdate" "rawpositionupdate" "smoothpositionupdate" "kalmanpositionupdate" "calculationupdate" "buttondownupdate"]]
    [:stopOutputIfTagIsNotSeenIn
     {:description "Optional, stops the output if tag hasn't been seen by specified time. In seconds. Minimum accepted value 5, maximum 60. Default is 'never'."
      :optional true
      :default "never"}
     [:or
      [:and :string [:= "never"]]
      [:int {:min 5 :max 60}]]]

    ;; Required if triggerMode = interval
    [:interval
     {:description "Required for triggerMode Interval. In seconds, can be a decimal. Minimum accepted value 0.1s, maximum 600s (10min)"
      :optional true}
     [:double {:min 0.1 :max 600}]]

    [:maxBatchSize
     {:description "Optional, applicable for 'Interval' type target. Defines how many tags / locators are collected into a single message when outputting data. Only collected over single interval to prevent additional delay. Note that large message size with UDP can easily cause end of the message being cut away."
      :optional true}
     [:int {:min 1 :max 10000000}]]

    [:onDataChange
     {:description "Optional, if set output is only produced if the value of the field specified has changed since previous output. For example $(location.ts) would mean that output is only produced if the Location estimate's timestamp is different that of previous output."
      :optional true}
     :string]

    ;; Required if target = udp
    [:ipAddress
     {:description "Required for target mode 'UDP', i.e. when creating UDP push, specifies the target IP address."
      :optional true}
     :string]
    [:port
     {:description "Required for target mode 'UDP', i.e. when creating UDP push, specifies the target port."
      :optional true}
     [:int {:min 1 :max 65535}]]

    ;; Required if target = file
    [:folder
     {:description "Required for target mode 'File', i.e. when outputting to a file, specifies the folder name to which logs are written."
      :optional true}
     :string]
    [:prefix
     {:description "Optional for target mode 'File', i.e. when outputting to a file. Specifies the filename prefix for the log output files. By default prefix is empty."
      :optional true}
     :string]
    [:includeHeader
     {:description "Optional for target mode 'File', i.e. when outputting to a file. If specified, writes a header for a new output file describing the output target."
      :optional true
      :default false}
     :boolean]
    [:tagGroups
     {:optional true
      :description "Optional, specify a comma separate list of tag groups. Tag groups must exist in the system. The output contains only tags in the specified tag groups."
      :decode/string #(str/split % #",")}
     [:* string?]]

    ;; For target = MQTT
    [:MQTTAddress
     {:description "Required for target mode 'MQTT'. Address URI of the MQTT broker server to connect to. Scheme must be included (one of: tcp, ssl, ws, wss). Port can be included, or use 'port' param."
      :optional true
      :decode/string #?(:clj #(java.net.URI. %) :cljs #(goog.Uri %))}
     uri?]
    [:MQTTClientId
     {:description "Optional for target mode 'MQTT'. Client ID of the MQTT publisher. If Client ID is left empty, the target name is used as a Client ID."
      :optional    true}
     :string]
    [:MQTTUsername
     {:description "Optional for target mode 'MQTT'. Username for authenticating the publisher client."
      :optional    true}
     :string]
    [:MQTTPassword
     {:description "Optional for target mode 'MQTT'. Password for authenticating the publisher client."
      :optional true}
     :string]
    [:MQTTTopicPrefix
     {:description "Optional for target mode 'MQTT'. Custom topic prefix before the standard Quuppa topic ('quuppa/apidata/...')"
      :optional true}
     :string]
    [:retain
     {:description "Optional, only for target mode 'MQTT'. Enables retain option."
      :optional true}
     :boolean]
    [:certificate-validation
     {:description "Optional, only for target mode 'MQTT'."
      :optional true}
     :string]
    [:client-key-passphrase
     {:description "Optional, only for target mode 'MQTT'. Passphrase for the encrypted client key file."
      :optional true}
     :string]
    [:MQTTAuthToken
     {:description "Optional for target mode 'MQTT'."
      :optional true}
     :string]
    [:MQTTAuthType
     {:description "Optional for target mode 'MQTT'."
      :optional true}
     :string]
    [:files
     {:description "Optional for target mode 'MQTT'."
      :optional true}
     :any]

    ;; For target = Websocket
    [:WebsocketAddress
     {:description ""
      :optional true
      :decode/string #?(:clj #(java.net.URI. %) :cljs #(goog.Uri %))}
     uri?]
    [:WebsocketUsername
     {:description ""
      :optional    true}
     :string]
    [:WebsocketPassword
     {:description ""
      :optional true}
     :string]
    [:WebsocketAuthorizationType
     {:description "Optional for target mode 'Websocket'."
      :optional true}
     :string]
    [:WebsocketAuthToken
     {:description "Optional for target mode 'Websocket'."
      :optional true}
     :string]
    ;; For target HTTPPost
    [:url
     {:description "Required for target mode 'HTTPPost'. URL where the data is sent. Needs to start with http:// or https://"
      :optional true}
     :string]
    [:headers
     {:description "Optional for target mode 'HTTPPost'. Headers to be added to the request, in format \"key\" = \"value1\", \"key2\" = \"value2\",..."
      :optional true}
     :string]

    ;; General params
    [:notes
     {:description "Optional, additional notes to this target."
      :optional true}
     :string]
    [:duration
     {:description "Optional, specifies time in seconds this target stays active once started. Target will be removed after it expires."
      :optional true
      :min 1}
     :int]
    [:start
     {:description "Optional, when specified target will start automatically once created."
      :optional true}
     :boolean]
    [:entriesPerFile
     {:optional true
      :default 100000}
     :int]]

   [:fn {:error/message "interval is required if triggerMode is \"interval\""
         :error/path [:triggerMode]}
    (fn [v]
      (if (= "interval" (:triggerMode v))
        (some? (:interval v))
        true))]

   [:fn {:error/message "ipAddress and port are required if target is \"udp\""
         :error/path [:target]}
    (fn [v]
      (if (= "udp" (:target v))
        (and (some? (:ipAddress v))
             (some? (:port v)))
        true))]

   ;[:fn {:error/message "folder is required if target is \"file\""
   ;      :error/path [:target]}
   ; (fn [v]
   ;   (if (= "file" (:target v))
   ;     (some? (:folder v))
   ;     true))]

   [:fn {:error/message "MQTTAddress is required if target is \"mqtt\""
         :error/path [:target]}
    (fn [v]
      (if (= "mqtt" (:target v))
        (some? (:MQTTAddress v))
        true))]
   [:fn {:error/message "Url is required if target is \"HTTPPost\""
         :error/path [:url]}
    (fn [v]
      (if (= "httppost" (:target v))
        (some? (:url v))
        true))]])

;; No default values here, if property isn't mentioned, it won't changed.
(def control-universal-target-schema
  [:map
   [:target
    ;; NOTE: this is target's 'name' actually
    {:description "Required. Specified output target must exist within the current project."}
    :string]
   [:mode
    {:description "Required. One of 'delete', 'start' or 'stop'. Set to 'delete' to delete Output Target with given id, 'start' to start an output target with given id, 'stop' to stop an output target with given id."
     :decode/json q-schema/->lower-case}
    [:enum "start" "stop" "delete"]]
   [:interval
    {:description "Optional. In seconds, can be a decimal. Minimum accepted value 0.1s, maximum 600s (10min)"
     :optional true}
    [:double
     {:min 0.1 :max 600}]]
   [:stopOutputIfTagIsNotSeenIn
    {:description "Optional, stops the output if tag hasn't been seen by specified time. In seconds. Minimum accepted value 5, maximum 60. Default is never."
     :optional true}
    [:or
     [:= "never"]
     [:double {:min 5 :max 60}]]]
   [:duration
    {:description "Total time the Output target is alive, in seconds."
     :optional true}
    :int]
   [:ipAddress
    {:description "Optional."
     :optional true}
    :string]
   [:port
    {:description "Optional."
     :optional true}
    [:int {:min (inc 1) :max (dec 65535)}]]
   [:folder
    {:description "Optional."
     :optional true}
    :string]
   [:prefix
    {:description "Optional."
     :optional true}
    :string]
   [:entriesPerFile
    {:optional true}
    :int]
   [:includeHeader
    {:optional true}
    :int]
   [:maxBatchSize
    {:description "Optional, applicable for 'Interval' type target. Defines how many tags / locators are collected into a single message when outputting data. Only collected over single interval to prevent additional delay. Note that large message size with UDP can easily cause end of the message being cut away."
     :optional true}
    [:int {:min 1 :max 10000000}]]

   [:onDataChange
    {:description "Optional, if set output is only produced if the value of the field specified has changed since previous output. For example $(location.ts) would mean that output is only produced if the Location estimate's timestamp is different that of previous output."
     :optional true}
    :string]

   ;; For target = MQTT
   [:MQTTAddress
    {:description "Required for target mode 'MQTT'. Address URI of the MQTT broker server to connect to. Scheme must be included (one of: tcp, ssl, ws, wss). Port can be included, or use 'port' param."
     :optional true
     :decode/string #?(:clj #(java.net.URI. %) :cljs #(goog.Uri %))}
    uri?]
   [:MQTTClientId
    {:description "Optional for target mode 'MQTT'"
     :optional    true}
    :string]
   [:MQTTUsername
    {:description "Optional for target mode 'MQTT'"
     :optional    true}
    :string]
   [:MQTTPassword
    {:description "Optional for target mode 'MQTT'"
     :optional true}
    :string]
   [:MQTTTopicPrefix
    {:description "Optional for target mode 'MQTT'"
     :optional true}
    :string]
   [:retain
    {:description "Optional, only for target mode 'MQTT'. Enables retain option."
     :optional true}
    :boolean]
   [:certificate-validation
    {:description "Optional, only for target mode 'MQTT'."
     :optional true}
    :string]
   [:client-key-passphrase
    {:description "Optional, only for target mode 'MQTT'. Passphrase for the encrypted client key file."
     :optional true}
    :string]
   [:MQTTAuthToken
    {:description "Optional for target mode 'MQTT'"
     :optional true}
    :string]
   [:MQTTAuthType
    {:description "Optional for target mode 'MQTT'."
     :optional true}
    :string]
   [:files
    {:description "Optional for target mode 'MQTT'."
     :optional true}
    :any]

   ;; For target = Websocket
   [:WebsocketAddress
    {:description ""
     :optional true
     :decode/string #?(:clj #(java.net.URI. %) :cljs #(goog.Uri %))}
    uri?]
   [:WebsocketUsername
    {:description ""
     :optional    true}
    :string]
   [:WebsocketPassword
    {:description ""
     :optional true}
    :string]
   [:WebsocketAuthorizationType
    {:description "Optional for target mode 'Websocket'."
     :optional true}
    :string]
   [:WebsocketAuthToken
    {:description "Optional for target mode 'Websocket'"
     :optional true}
    :string]
   ;; For target HTTPPost
   [:url
    {:description "Required for target mode 'HTTPPost'. URL where the data is sent. Needs to start with http:// or https://"
     :optional true}
    :string]])
